import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fetchWithAuth } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';
import { Newspaper, Users, ArrowLeft } from 'lucide-react'; // Removed Award import since we're removing that tab
import { Button } from 'src/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'src/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/components/ui/tabs';
import DebaterView from '../components/round/DebaterView';
import AdjudicatorView from '../components/round/AdjudicatorView';
import PanelistView from './round/PanelistView'; // Add this import
import TraineeView from './round/TraineeView'; // Add this import
import toast from 'react-hot-toast';
import { formatMotion } from '../utils/format-helpers'; // Add the import
import RatingStatusMonitor from './round/RatingStatusMonitor'; // Replace RatingStatusProvider with RatingStatusMonitor
import RatingEventBus from '../services/RatingEventBus'; // Add import

// Common round information interface
interface RoundInfo {
  id: number;
  format: string;
  status: string;
  round_code: string;
  motion?: {
    text: string;
    theme?: {
      name: string;
    }
  };
  participants: Array<{
    id: number;
    user_id: number;
    role: string;
    status: string;
    user: {
      id: number;
      username: string;
      first_name?: string;
      last_name?: string;
    }
  }>;
  your_role?: string;
  current_user_id?: number;
}

const ActiveRound: React.FC = () => {
  const { roundId } = useParams<{ roundId: string }>();
  const navigate = useNavigate();
  const { pageTransition } = useTheme();
  
  const [roundData, setRoundData] = useState<RoundInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userRole, setUserRole] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('info');
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.3,
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    },
    exit: { opacity: 0, transition: { duration: 0.3 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.3 } }
  };

  /* eslint-disable @typescript-eslint/no-unused-vars */
  const fetchRatingStatus = useCallback(async () => {
    if (!roundId || !roundData || userRole !== 'Chair Adjudicator') {
      return null;
    }

    try {
      const data = await fetchWithAuth(`/api/round/${roundId}/rating-status/`);
      return data;
    } catch (err) {
      console.log('Error fetching rating status:', err);
      return null;
    }
  }, [roundId, roundData, userRole]);

  const checkRatingStatusForChair = useCallback(async () => {
    if (userRole !== 'Chair Adjudicator') return;
    
    // Use the event bus instead of direct API call
    RatingEventBus.publish('STATUS_UPDATE_NEEDED', { roundId });
  }, [userRole, roundId]);
  /* eslint-enable @typescript-eslint/no-unused-vars */

  // Extract the fetchRoundData function outside useEffect so it can be used elsewhere
  const fetchRoundData = useCallback(async (skipStatusCheck = false) => {
    // If already loaded once, don't fetch again to preserve user entries
    if (initialLoadComplete && !skipStatusCheck) {
      return;
    }
    
    try {
      setLoading(true);
      setError("");
      
      let data = null;
      let success = false;
      
      // Define endpoints to try in order
      const endpoints = [
        `/api/rounds/${roundId}/`,
        `/api/round/${roundId}/`,
        `/round/${roundId}/`
      ];
      
      for (const endpoint of endpoints) {
        try {
          console.log(`Trying endpoint: ${endpoint}`);
          data = await fetchWithAuth(endpoint);
          success = true;
          console.log(`Successfully fetched round data from ${endpoint}`);
          break;
        } catch (err) {
          console.log(`Failed with endpoint ${endpoint}:`, err);
        }
      }
      
      if (!success || !data) {
        throw new Error("Failed to fetch round data from all endpoints");
      }
      
      console.log("Round data:", data);
      
      // If round is not active, redirect to appropriate page
      // Only check status on first load or when explicitly requested
      if (skipStatusCheck || !initialLoadComplete) {
        // Convert status to lowercase for case-insensitive comparison
        const status = data.status?.toLowerCase();
        
        if (status === 'setup' || status === 'waiting' || status === 'allocation') {
          pageTransition(() => {
            navigate(`/round/${roundId}/setup`);
          });
          return;
        }
        
        if (status === 'completed') {
          pageTransition(() => {
            // Redirect everyone to the robust results endpoint
            navigate(`/round/${roundId}/results`);
          });
          return;
        }
      }
      
      // Only update round data if this is the initial load
      // This prevents ratings/adjudications from being reset
      if (!initialLoadComplete) {
        setRoundData(data);
        
        // Determine user's role
        const currentUserId = data.current_user_id;
        const participant = data.participants?.find((p: {user_id: number}) => p.user_id === currentUserId);
        const role = participant?.role || null;
        
        setUserRole(role);
        console.log("User role:", role);
        
        // Mark initial load as complete
        setInitialLoadComplete(true);
      } else if (skipStatusCheck) {
        // Only update round status if explicitly checking status
        if (data && roundData) {
          setRoundData({...roundData, status: data.status});
        }
      }
      
    } catch (err: any) {
      console.error("Error fetching round data:", err);
      setError(err.message || "Failed to fetch round data");
      
      // Add a recovery mechanism to redirect back to dashboard if all endpoints fail
      setTimeout(() => {
        toast.error("Could not load round data. Returning to dashboard...");
        pageTransition(() => {
          navigate("/dashboard");
        });
      }, 3000); // Give user 3 seconds to see the error before redirecting
    } finally {
      setLoading(false);
    }
  }, [roundId, navigate, pageTransition, initialLoadComplete, roundData]);

  useEffect(() => {
    fetchRoundData();
    
    // Remove the rating status interval as AdjudicatorView handles this
    // Only keep the round status check interval
    const statusCheckInterval = setInterval(() => {
      fetchRoundData(true); // Only check status, don't update full data
    }, 60000); // Check status every 60 seconds
    
    return () => {
      clearInterval(statusCheckInterval);
    };
  }, [fetchRoundData]);

  useEffect(() => {
    if (activeTab === "info" && userRole === 'Chair Adjudicator') {
      checkRatingStatusForChair();
    }
  }, [activeTab, userRole, checkRatingStatusForChair]);

  const handleBack = () => {
    pageTransition(() => {
      navigate("/dashboard");
    });
  };

  // Group participants by role categories
  const getParticipantsByCategory = () => {
    if (!roundData || !roundData.participants) return {
      debaters: [],
      adjudicators: [],
      spectators: []
    };
    
    const adjudicatorRoles = ['Chair Adjudicator', 'Panelist', 'Trainee'];
    
    return {
      debaters: roundData.participants.filter(p => !adjudicatorRoles.includes(p.role) && p.role !== 'Spectator'),
      adjudicators: roundData.participants.filter(p => adjudicatorRoles.includes(p.role)),
      spectators: roundData.participants.filter(p => p.role === 'Spectator')
    };
  };

  // Determine if user is adjudicator
  const isAdjudicator = useCallback(() => {
    const adjudicatorRoles = ['Chair Adjudicator', 'Panelist', 'Trainee'];
    return userRole && adjudicatorRoles.includes(userRole);
  }, [userRole]);

  // Determine if user is debater  
  const isDebater = () => {
    const debaterRoles = [
      'Prime Minister', 'Deputy Prime Minister', 'Government Whip', 'Member of Government',
      'Leader of Opposition', 'Deputy Leader of Opposition', 'Opposition Whip', 'Member of Opposition'
    ];
    return debaterRoles.includes(userRole || '');
  };

  if (loading && !initialLoadComplete) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error && !initialLoadComplete) {
    return (
      <div className="p-6">
        <div className="p-4 bg-red-500 bg-opacity-10 border border-red-500 rounded-lg">
          <h3 className="text-lg font-semibold text-red-500">Error</h3>
          <p className="text-red-500 mb-4">{error}</p>
          <div className="flex gap-2">
            <Button onClick={() => fetchRoundData(false)}>Try Again</Button>
            <Button variant="outline" onClick={handleBack}>Back to Dashboard</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Add the monitor at the top of the component */}
      {roundId && <RatingStatusMonitor roundId={roundId} componentId="active-round" />}
      
      <motion.div 
        className="p-4"
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={containerVariants}
      >
        <Card className="w-full max-w-6xl mx-auto shadow-lg rounded-lg transition-colors duration-200 dark:bg-gray-800 light:bg-white">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div className="flex items-center">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleBack}
                className="mr-2"
              >
                <ArrowLeft size={16} />
              </Button>
              <div>
                <CardTitle className="text-2xl font-bold dark:text-white light:text-gray-800">
                  Active Round
                </CardTitle>
                <p className="text-gray-400 text-sm">
                  {roundData?.format === 'BP' ? 'British Parliamentary' : 'Asian Parliamentary'} Format • 
                  Your Role: <span className="font-semibold">{userRole || 'Observer'}</span>
                </p>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="p-6">
            {roundData && (
              <motion.div variants={itemVariants} className="space-y-6">
                {/* Common Round Info Section */}
                <div className="p-4 rounded-lg border-2 border-blue-500 bg-blue-900 bg-opacity-10">
                  <h3 className="text-lg font-semibold dark:text-white light:text-gray-800 mb-4">Round Information</h3>
                  <div>
                    <p className="text-sm text-gray-400">Motion</p>
                    <p className="dark:text-white light:text-gray-800 font-medium text-lg">
                      {formatMotion(roundData.motion)}
                    </p>
                    {roundData.motion?.theme?.name && (
                      <p className="text-sm text-blue-500 mt-1">Theme: {roundData.motion.theme.name}</p>
                    )}
                  </div>
                </div>
                
                {/* Main Content Area - Different for each role */}
                <div className="space-y-6">
                  <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="grid grid-cols-2">
                      <TabsTrigger value="info" className="flex items-center">
                        <Newspaper className="mr-2" size={16} />
                        Round Info
                      </TabsTrigger>
                      <TabsTrigger value="participants" className="flex items-center">
                        <Users className="mr-2" size={16} />
                        Participants
                      </TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="info" className="space-y-4 mt-4">
                      <div className="space-y-4">
                        {isDebater() && (
                          <DebaterView 
                            roundData={roundData}
                            userRole={userRole || ''}
                            roundId={roundId || ''}
                          />
                        )}
                        
                        {isAdjudicator() && (
                          <>
                            {userRole === 'Chair Adjudicator' && (
                              <AdjudicatorView 
                                roundData={roundData}
                                userRole={userRole}
                                roundId={roundId || ''}
                              />
                            )}
                            
                            {userRole === 'Panelist' && (
                              <PanelistView
                                roundData={roundData}
                                roundId={roundId || ''}
                              />
                            )}
                            
                            {userRole === 'Trainee' && (
                              <TraineeView
                                roundData={roundData}
                                roundId={roundId || ''}
                              />
                            )}
                          </>
                        )}
                        
                        {!isDebater() && !isAdjudicator() && (
                          <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-lg">
                            <h3 className="font-medium mb-2">Spectator View</h3>
                            <p>You are viewing this round as a spectator.</p>
                          </div>
                        )}
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="participants" className="space-y-4 mt-4">
                      <div className="space-y-6">
                        {/* Debaters */}
                        <div>
                          <h3 className="text-xl font-semibold mb-3 flex items-center">
                            <Users className="mr-2" size={20} />
                            Debaters
                          </h3>
                          
                          {roundData.format === 'BP' ? (
                            // BP Format Teams
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-3">
                                <h4 className="font-medium text-blue-400">Opening Government</h4>
                                <div className="border border-blue-900 rounded-lg p-3 bg-blue-900 bg-opacity-10">
                                  {getParticipantsByCategory().debaters
                                    .filter(p => ['Prime Minister', 'Deputy Prime Minister'].includes(p.role))
                                    .map(p => (
                                      <div key={p.id} className="flex justify-between items-center p-2 mb-1">
                                        <div>
                                          <p className="font-medium">{p.user.username}</p>
                                          <p className="text-xs text-gray-400">{p.role}</p>
                                        </div>
                                      </div>
                                    ))}
                                </div>
                                
                                <h4 className="font-medium text-red-400 mt-3">Opening Opposition</h4>
                                <div className="border border-red-900 rounded-lg p-3 bg-red-900 bg-opacity-10">
                                  {getParticipantsByCategory().debaters
                                    .filter(p => ['Leader of Opposition', 'Deputy Leader of Opposition'].includes(p.role))
                                    .map(p => (
                                      <div key={p.id} className="flex justify-between items-center p-2 mb-1">
                                        <div>
                                          <p className="font-medium">{p.user.username}</p>
                                          <p className="text-xs text-gray-400">{p.role}</p>
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              </div>
                              
                              <div className="space-y-3">
                                <h4 className="font-medium text-blue-400">Closing Government</h4>
                                <div className="border border-blue-900 rounded-lg p-3 bg-blue-900 bg-opacity-10">
                                  {getParticipantsByCategory().debaters
                                    .filter(p => ['Government Whip', 'Member of Government'].includes(p.role))
                                    .map(p => (
                                      <div key={p.id} className="flex justify-between items-center p-2 mb-1">
                                        <div>
                                          <p className="font-medium">{p.user.username}</p>
                                          <p className="text-xs text-gray-400">{p.role}</p>
                                        </div>
                                      </div>
                                    ))}
                                </div>
                                
                                <h4 className="font-medium text-red-400 mt-3">Closing Opposition</h4>
                                <div className="border border-red-900 rounded-lg p-3 bg-red-900 bg-opacity-10">
                                  {getParticipantsByCategory().debaters
                                    .filter(p => ['Opposition Whip', 'Member of Opposition'].includes(p.role))
                                    .map(p => (
                                      <div key={p.id} className="flex justify-between items-center p-2 mb-1">
                                        <div>
                                          <p className="font-medium">{p.user.username}</p>
                                          <p className="text-xs text-gray-400">{p.role}</p>
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              </div>
                            </div>
                          ) : (
                            // AP Format Teams
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <h4 className="font-medium text-blue-400">Government</h4>
                                <div className="border border-blue-900 rounded-lg p-3 bg-blue-900 bg-opacity-10">
                                  {getParticipantsByCategory().debaters
                                    .filter(p => ['Prime Minister', 'Deputy Prime Minister', 'Government Whip'].includes(p.role))
                                    .map(p => (
                                      <div key={p.id} className="flex justify-between items-center p-2 mb-1">
                                        <div>
                                          <p className="font-medium">{p.user.username}</p>
                                          <p className="text-xs text-gray-400">{p.role}</p>
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              </div>
                              
                              <div>
                                <h4 className="font-medium text-red-400">Opposition</h4>
                                <div className="border border-red-900 rounded-lg p-3 bg-red-900 bg-opacity-10">
                                  {getParticipantsByCategory().debaters
                                    .filter(p => ['Leader of Opposition', 'Deputy Leader of Opposition', 'Opposition Whip'].includes(p.role))
                                    .map(p => (
                                      <div key={p.id} className="flex justify-between items-center p-2 mb-1">
                                        <div>
                                          <p className="font-medium">{p.user.username}</p>
                                          <p className="text-xs text-gray-400">{p.role}</p>
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Adjudicators */}
                        <div>
                          <h3 className="text-xl font-semibold mb-3 flex items-center">
                            <Users className="mr-2" size={20} />
                            Adjudicators
                          </h3>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <h4 className="font-medium text-purple-400">Chair Adjudicator</h4>
                              <div className="border border-purple-900 rounded-lg p-3 bg-purple-900 bg-opacity-10">
                                {getParticipantsByCategory().adjudicators
                                  .filter(p => p.role === 'Chair Adjudicator')
                                  .map(p => (
                                    <div key={p.id} className="flex justify-between items-center p-2 mb-1">
                                      <div>
                                        <p className="font-medium">{p.user.username}</p>
                                        <p className="text-xs text-gray-400">{p.role}</p>
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            </div>
                            
                            <div>
                              <h4 className="font-medium text-purple-400">Panelists</h4>
                              <div className="border border-purple-900 rounded-lg p-3 bg-purple-900 bg-opacity-10">
                                {getParticipantsByCategory().adjudicators
                                  .filter(p => p.role === 'Panelist')
                                  .map(p => (
                                    <div key={p.id} className="flex justify-between items-center p-2 mb-1">
                                      <div>
                                        <p className="font-medium">{p.user.username}</p>
                                        <p className="text-xs text-gray-400">{p.role}</p>
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            </div>
                            
                            <div>
                              <h4 className="font-medium text-purple-400">Trainees</h4>
                              <div className="border border-purple-900 rounded-lg p-3 bg-purple-900 bg-opacity-10">
                                {getParticipantsByCategory().adjudicators
                                  .filter(p => p.role === 'Trainee')
                                  .map(p => (
                                    <div key={p.id} className="flex justify-between items-center p-2 mb-1">
                                      <div>
                                        <p className="font-medium">{p.user.username}</p>
                                        <p className="text-xs text-gray-400">{p.role}</p>
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              </motion.div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </>
  );
};

export default ActiveRound;